/**
 * Hooks for Living SOPs proposals (Phase 23.1.6 — fallback editor)
 *
 * Manages TanStack Query mutations for the "Edit (advanced)" modal on the
 * SOP detail page:
 *  - useAcquireEditLock — POST /api/sops/:sopId/edit-lock/acquire
 *  - useReleaseEditLock — DELETE /api/sops/:sopId/edit-lock
 *  - useCreateManualProposal — POST /api/sops/:sopId/proposals/manual
 *
 * The lock acquire mutation can reject with a 409 (CONFLICT) carrying
 * `{ otherUser: { id, name } | null, expiresAt: string }` details. The modal
 * reads those details to display the "[name] is editing this SOP" banner.
 *
 * Follows the same TanStack Query pattern as useSectionReview.ts.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  SopProposalChanges,
  ProposalEvidence,
} from '@guild/shared';

export interface EditLockAcquireSuccess {
  sopId: string;
  expiresAt: string;
}

export interface EditLockConflictDetails {
  otherUser: { id: string; name: string } | null;
  expiresAt: string;
}

export interface ManualProposalCreateSuccess {
  proposalId: string;
  state: 'pending_review' | 'approved' | 'rejected';
}

export const sopProposalKeys = {
  all: ['sop-proposals'] as const,
  list: () => [...sopProposalKeys.all, 'list'] as const,
  bySop: (sopId: string) => [...sopProposalKeys.all, 'sop', sopId] as const,
};

/**
 * Acquire or extend a 30-min edit lock for the fallback editor.
 *
 * On success: returns { sopId, expiresAt }.
 * On 409: error carries details { otherUser, expiresAt } — consumer should
 * inspect `err.details` to surface the "someone else is editing" banner.
 */
export function useAcquireEditLock(sopId: string) {
  const api = useApi();
  return useMutation<EditLockAcquireSuccess, Error, void>({
    mutationFn: () =>
      api.post<EditLockAcquireSuccess>(
        `/api/sops/${sopId}/edit-lock/acquire`
      ),
  });
}

/**
 * Release the caller's edit lock. Fire-and-forget — best-effort cleanup
 * when the modal closes.
 */
export function useReleaseEditLock(sopId: string) {
  const api = useApi();
  return useMutation<{ released: boolean }, Error, void>({
    mutationFn: () =>
      api.delete<{ released: boolean }>(`/api/sops/${sopId}/edit-lock`),
  });
}

/**
 * Submit a trainer-authored proposal to the 23.2 inbox.
 * Invalidates the proposals list on success so the inbox refreshes when
 * it ships.
 */
export function useCreateManualProposal(sopId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    ManualProposalCreateSuccess,
    Error,
    {
      changes: SopProposalChanges;
      rationale: string;
      evidence?: ProposalEvidence;
    }
  >({
    mutationFn: (body) =>
      api.post<ManualProposalCreateSuccess>(
        `/api/sops/${sopId}/proposals/manual`,
        body
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sopProposalKeys.all });
    },
  });
}
